import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL;

const TYPE_LABELS = {
  escape_room: "🔐 Escape Room",
  boutique_jeu: "🎲 Boutique de jeux",
  other: "📦 Autre",
};

const STATUS_COLORS = {
  active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  paused: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  terminated: "bg-red-500/20 text-red-300 border-red-500/30",
};

function fmtEur(centimes) {
  return ((centimes || 0) / 100).toFixed(2) + " €";
}

export default function AffiliatesPanel({ token, toast }) {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/affiliates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAffiliates(data);
    } catch {
      toast.error("Erreur chargement affiliés");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAffiliates(); }, [fetchAffiliates]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-500">{affiliates.length} affilié(s)</p>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition"
        >
          + Ajouter un affilié
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Chargement…</p>
      ) : affiliates.length === 0 ? (
        <p className="text-gray-500 italic text-sm">Aucun affilié pour l'instant.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full text-sm">
            <thead className="bg-white/5 text-gray-400 uppercase text-xs">
              <tr>
                <th className="py-3 px-4 text-left">Nom</th>
                <th className="py-3 px-4 text-left">Type</th>
                <th className="py-3 px-4 text-left">Code</th>
                <th className="py-3 px-4 text-left">Taux</th>
                <th className="py-3 px-4 text-left">Statut</th>
                <th className="py-3 px-4 text-right">Ventes / mois</th>
                <th className="py-3 px-4 text-right">CA mois</th>
                <th className="py-3 px-4 text-right">Comm. mois</th>
                <th className="py-3 px-4 text-right">À verser</th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => navigate(`/admin/affiliates/${a.id}`)}
                  className="border-t border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                >
                  <td className="py-3 px-4 font-medium text-white">{a.name}</td>
                  <td className="py-3 px-4 text-gray-300 text-xs">{TYPE_LABELS[a.type] || a.type}</td>
                  <td className="py-3 px-4 font-mono text-indigo-300">{a.couponCode}</td>
                  <td className="py-3 px-4 text-amber-300">{(a.commissionRate * 100).toFixed(0)}%</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[a.status]}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-gray-300">{a.stats.salesMonth}</td>
                  <td className="py-3 px-4 text-right text-gray-300">{fmtEur(a.stats.monthAmount)}</td>
                  <td className="py-3 px-4 text-right text-emerald-300">{fmtEur(a.stats.monthCommission)}</td>
                  <td className="py-3 px-4 text-right font-semibold text-amber-300">
                    {fmtEur(a.stats.unpaidCommission)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <NewAffiliateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchAffiliates(); }}
          token={token}
          toast={toast}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// MODALE CRÉATION (avec Portal)
// ─────────────────────────────────────────────
function NewAffiliateModal({ onClose, onCreated, token, toast }) {
  const [form, setForm] = useState({
    name: "",
    type: "escape_room",
    contactName: "",
    email: "",
    phone: "",
    couponCode: "",
    commissionRate: 0.10,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Empêcher le scroll du body quand modale ouverte
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Fermer avec Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/affiliates`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Affilié créé ✅ Coupon Stripe généré.");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur création");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center sticky top-0 bg-gray-900 rounded-t-2xl">
          <h3 className="text-lg font-semibold text-white">Nouvel affilié</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Field label="Nom de la boîte *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />

          <div>
            <label className="block text-xs text-gray-400 mb-1">Type *</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white"
            >
              <option value="escape_room">🔐 Escape Room</option>
              <option value="boutique_jeu">🎲 Boutique de jeux</option>
              <option value="other">📦 Autre</option>
            </select>
          </div>

          <Field label="Nom du contact" value={form.contactName} onChange={(v) => setForm({ ...form, contactName: v })} />
          <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          <Field label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />

          <div>
            <label className="block text-xs text-gray-400 mb-1">
              Code promo * <span className="text-gray-500">(visible par les clients, ex: LYONESCAPE)</span>
            </label>
            <input
              value={form.couponCode}
              onChange={(e) => setForm({ ...form, couponCode: e.target.value.toUpperCase().replace(/\s+/g, "_") })}
              placeholder="LYONESCAPE"
              required
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white font-mono"
            />
            <p className="text-[10px] text-gray-500 mt-1">Lettres, chiffres, _ et - uniquement. Sera créé automatiquement dans Stripe.</p>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Taux de commission</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={form.commissionRate}
              onChange={(e) => setForm({ ...form, commissionRate: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white"
            />
            <p className="text-[10px] text-gray-500 mt-1">0.10 = 10%. Modifiable plus tard.</p>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm">
              Annuler
            </button>
            <button type="submit" disabled={submitting} className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm disabled:opacity-50">
              {submitting ? "Création..." : "Créer l'affilié"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white"
      />
    </div>
  );
}